import { Component, DestroyRef, Injector, Input, OnInit, Signal, computed, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GlobalSettingsFormShape } from '../../components/smart-cart-form-global-settings/global-settings.model';
import {
  DISCOUNT_CODES_STARTER_TEMPLATE,
  DISCOUNT_CODES_VARIABLES,
  DiscountCodesFormShape,
} from './discount-codes.model';

@Component({
  selector: 'smart-cart-form-discount-codes-editor',
  templateUrl: './discount-codes.component.html',
  styleUrls: ['./discount-codes.component.scss'],
  standalone: false,
})
export class SmartCartFormDiscountCodesEditorComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<DiscountCodesFormShape>;
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  @Input() usageToken = '';

  readonly starterTemplate = DISCOUNT_CODES_STARTER_TEMPLATE;
  readonly customCodeVariables = DISCOUNT_CODES_VARIABLES;

  codeTypesExpanded = true;
  labelsExpanded = true;
  designExpanded = true;

  // Display controls show `widget.value ?? global.value`. Edits go to the underlying
  // widget control, which marks it as overridden. Initialized eagerly so the template
  // always has a defined FormControl to bind to (ngOnInit only wires up the sync).
  readonly displayBackgroundColor = new FormControl<string>('', { nonNullable: true });
  readonly displayTextColor = new FormControl<string>('', { nonNullable: true });
  readonly displayHoverColor = new FormControl<string>('', { nonNullable: true });
  readonly displayCornerRadius = new FormControl<number>(0, { nonNullable: true });

  backgroundColorOverridden!: Signal<boolean>;
  textColorOverridden!: Signal<boolean>;
  hoverColorOverridden!: Signal<boolean>;
  cornerRadiusOverridden!: Signal<boolean>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    const widget = this.formGroup.controls.design.controls;
    const global = this.globalSettingsFormGroup.controls.buttonDesign.controls;

    this.wireDisplayString(this.displayBackgroundColor, widget.backgroundColor, global.backgroundColor);
    this.wireDisplayString(this.displayTextColor, widget.textColor, global.textColor);
    this.wireDisplayString(this.displayHoverColor, widget.hoverColor, global.hoverTextColor);
    this.wireDisplayNumber(this.displayCornerRadius, widget.cornerRadius, global.cornerRadius);

    this.backgroundColorOverridden = this.overriddenSignal(widget.backgroundColor);
    this.textColorOverridden = this.overriddenSignal(widget.textColor);
    this.hoverColorOverridden = this.overriddenSignal(widget.hoverColor);
    this.cornerRadiusOverridden = this.overriddenSignal(widget.cornerRadius);
  }

  toggleCodeTypes(): void {
    this.codeTypesExpanded = !this.codeTypesExpanded;
  }

  toggleLabels(): void {
    this.labelsExpanded = !this.labelsExpanded;
  }

  toggleDesign(): void {
    this.designExpanded = !this.designExpanded;
  }

  resetToGlobal(widgetCtrl: AbstractControl): void {
    widgetCtrl.setValue(null);
  }

  private wireDisplayString(
    display: FormControl<string>,
    widgetCtrl: FormControl<string | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as string), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      // Empty input ↔ inherit (null), so clearing reverts to the global fallback.
      const value = next || null;
      if (widgetCtrl.value !== value) widgetCtrl.setValue(value);
    });
  }

  private wireDisplayNumber(
    display: FormControl<number>,
    widgetCtrl: FormControl<number | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as number), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      if (widgetCtrl.value !== next) widgetCtrl.setValue(next);
    });
  }

  private overriddenSignal(widgetCtrl: AbstractControl): Signal<boolean> {
    const value = toSignal(widgetCtrl.valueChanges, {
      initialValue: widgetCtrl.value,
      injector: this.injector,
    });
    return computed(() => value() !== null);
  }
}
